import { useState, useEffect } from 'react';
import { supabase } from '../providers/supabase/supabase';

// Types for raw GEMS data from gems_complete_analysis table
interface RawGEMSData {
    id: number;
    isrc: string;
    analyzed_at: string;
    lufs_integrated: number;
    loudness_range: number;
    bpm: number;
    beat_strength: number;
    onset_rate: number;
    key: string;
    scale: string;
    key_strength: number;
    brightness: number;
    brightness_variance: number;
    spectral_rolloff: number;
    spectral_complexity: number;
    zcr: number;
    bass_ratio: number;
    low_mid_ratio: number;
    mid_ratio: number;
    high_mid_ratio: number;
    energy: number;
    dynamic_range: number;
    crest_factor: number;
    spectral_flux: number;
    dissonance: number;
    attack_time: number;
    compression_amount: number;
    danceability: number;
    genre_confidence: number;
    genre_match_score: number;
    distinctive_features: string[];
    sonic_signature: string[];
    tonal_deviation_score: number;
    brightness_character: string;
    bass_character: string;
    sub_ratio: number;
    presence_ratio: number;
    air_ratio: number;
    overall_description: string;
    genre_positioning_score: number;
    genre_deviation_score: number;
    emotional_signature: string;
    emotion_1: string;
    emotion_1_score: number;
    emotion_2: string;
    emotion_2_score: number;
    emotion_3: string;
    emotion_3_score: number;
    emotion_4: string;
    emotion_4_score: number;
    primary_genre: string;
    secondary_genre: string;
    tonal_balance: string;
    danceability_description: string;
    sonic_signature_text: string;
    distinctive_features_text: string;
    tempo_description: string;
    is_instrumental: boolean;
    instrumental_confidence: number;
    instrumental_method: string;
    fallback_used: boolean;
    genre_used_for_analysis: string;
    tonal_balance_description: string;
}

// Clean, typed interface for components
export interface GEMSAnalysis {
    isrc: string;
    analyzed_at: Date;
    
    // Core emotional intelligence
    emotional_signature: string;
    emotion_1: string;
    emotion_1_score: number;
    emotion_2: string;
    emotion_2_score: number;
    emotion_3: string;
    emotion_3_score: number;
    emotion_4: string;
    emotion_4_score: number;
    
    // Sonic signature
    sonic_signature_text: string;
    distinctive_features_text: string;
    overall_description: string;
    
    // Audio characteristics
    bpm: number;
    key: string;
    scale: string;
    brightness: number;
    bass_ratio: number;
    energy: number;
    danceability: number;
    
    // Advanced audio features
    lufs_integrated: number;
    dynamic_range: number;
    spectral_complexity: number;
    dissonance: number;
    
    // Frequency breakdown
    sub_ratio: number;
    low_mid_ratio: number;
    mid_ratio: number;
    high_mid_ratio: number;
    presence_ratio: number;
    air_ratio: number;
    
    // Character descriptions
    brightness_character: string;
    bass_character: string;
    tonal_balance: string;
    tempo_description: string;
    danceability_description: string;
    
    // Genre analysis
    primary_genre: string;
    secondary_genre: string;
    genre_positioning_score: number;
    genre_confidence: number;
    
    // Technical info
    is_instrumental: boolean;
    instrumental_confidence: number;
}

// Transform raw GEMS data
const transformGEMSData = (raw: RawGEMSData): GEMSAnalysis => {
    return {
        isrc: raw.isrc,
        analyzed_at: new Date(raw.analyzed_at),
        
        // Emotional intelligence
        emotional_signature: raw.emotional_signature || '',
        emotion_1: raw.emotion_1 || '',
        emotion_1_score: raw.emotion_1_score || 0,
        emotion_2: raw.emotion_2 || '',
        emotion_2_score: raw.emotion_2_score || 0,
        emotion_3: raw.emotion_3 || '',
        emotion_3_score: raw.emotion_3_score || 0,
        emotion_4: raw.emotion_4 || '',
        emotion_4_score: raw.emotion_4_score || 0,
        
        // Sonic signature
        sonic_signature_text: raw.sonic_signature_text || '',
        distinctive_features_text: raw.distinctive_features_text || '',
        overall_description: raw.overall_description || '',
        
        // Audio characteristics
        bpm: raw.bpm || 0,
        key: raw.key || '',
        scale: raw.scale || '',
        brightness: raw.brightness || 0,
        bass_ratio: raw.bass_ratio || 0,
        energy: raw.energy || 0,
        danceability: raw.danceability || 0,
        
        // Advanced features
        lufs_integrated: raw.lufs_integrated || 0,
        dynamic_range: raw.dynamic_range || 0,
        spectral_complexity: raw.spectral_complexity || 0,
        dissonance: raw.dissonance || 0,
        
        // Frequency breakdown
        sub_ratio: raw.sub_ratio || 0,
        low_mid_ratio: raw.low_mid_ratio || 0,
        mid_ratio: raw.mid_ratio || 0,
        high_mid_ratio: raw.high_mid_ratio || 0,
        presence_ratio: raw.presence_ratio || 0,
        air_ratio: raw.air_ratio || 0,
        
        // Character descriptions
        brightness_character: raw.brightness_character || '',
        bass_character: raw.bass_character || '',
        tonal_balance: raw.tonal_balance || '',
        tempo_description: raw.tempo_description || '',
        danceability_description: raw.danceability_description || '',
        
        // Genre analysis
        primary_genre: raw.primary_genre || '',
        secondary_genre: raw.secondary_genre || '',
        genre_positioning_score: raw.genre_positioning_score || 0,
        genre_confidence: raw.genre_confidence || 0,
        
        // Technical info
        is_instrumental: raw.is_instrumental || false,
        instrumental_confidence: raw.instrumental_confidence || 0,
    };
};

export const useGEMSAnalysis = (artistId: string | null | undefined) => {
    const [gemsData, setGemsData] = useState<GEMSAnalysis[]>([]);
    const [topTrackGEMS, setTopTrackGEMS] = useState<GEMSAnalysis | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!artistId) {
            setGemsData([]);
            setTopTrackGEMS(null);
            return;
        }

        const fetchGEMSAnalysis = async () => {
            setLoading(true);
            setError(null);

            try {
                // First get the artist's tracks to find their ISRCs
                const { data: tracksData, error: tracksError } = await supabase
                    .from('tracks')
                    .select('isrc, top_track, spotify_plays')
                    .eq('artist_id', artistId)
                    .not('isrc', 'is', null)
                    .order('spotify_plays', { ascending: false });

                if (tracksError) throw tracksError;

                if (!tracksData || tracksData.length === 0) {
                    setGemsData([]);
                    setTopTrackGEMS(null);
                    return;
                }

                // Get GEMS analysis for all tracks
                const isrcs = tracksData.map(track => track.isrc);
                
                const { data: gemsAnalysis, error: gemsError } = await supabase
                    .from('gems_complete_analysis')
                    .select('*')
                    .in('isrc', isrcs)
                    .not('emotional_signature', 'is', null)
                    .order('analyzed_at', { ascending: false });

                if (gemsError) throw gemsError;

                if (!gemsAnalysis || gemsAnalysis.length === 0) {
                    setGemsData([]);
                    setTopTrackGEMS(null);
                    return;
                }

                // Transform the data
                const transformedGEMS = gemsAnalysis.map(transformGEMSData);
                setGemsData(transformedGEMS);

                // Find GEMS data for the top track (highest plays)
                const topTrackISRC = tracksData[0]?.isrc;
                const topGEMS = transformedGEMS.find(gems => gems.isrc === topTrackISRC);
                setTopTrackGEMS(topGEMS || transformedGEMS[0] || null);

            } catch (err) {
                console.error('Error fetching GEMS analysis:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch GEMS analysis');
                setGemsData([]);
                setTopTrackGEMS(null);
            } finally {
                setLoading(false);
            }
        };

        fetchGEMSAnalysis();
    }, [artistId]);

    return { 
        gemsData, 
        topTrackGEMS, 
        loading, 
        error 
    };
};